import { useEffect } from "react";

import { NAVY, SHEET_OFFSCREEN } from "./tokens";

/**
 * The handful of rules a style attribute cannot express: the hidden scrollbar,
 * the focus ring, the two entrances, and the reduced-motion answer to both.
 *
 * Injected once into `<head>` rather than written as Tailwind classes, because
 * the app's Tailwind build does not scan this package, and rather than added to
 * a stylesheet, because the two consumers load different ones: the Discover
 * sheet gets `discover.css`, the account modal and the concierge do not.
 *
 * Only transform and opacity move. Nothing runs over 300ms. Nothing eases in.
 */
const STYLE_ID = "mj-ui-menu-styles";

const OUT = "cubic-bezier(0.23, 1, 0.32, 1)";
const DRAWER = "cubic-bezier(0.32, 0.72, 0, 1)";

const CSS = `
.mj-ui-scroll { scrollbar-width: none; -ms-overflow-style: none; }
.mj-ui-scroll::-webkit-scrollbar { width: 0; height: 0; display: none; }

.mj-ui-row { -webkit-tap-highlight-color: transparent; }
.mj-ui-row:focus-visible { outline: 2px solid ${NAVY}; outline-offset: -2px; }

.mj-ui-press { transition: transform 160ms ${OUT}; }
.mj-ui-press:active { transform: scale(0.97); }

/* A menu grows out of the corner it hangs from. Opening a second menu while
   one is open sets data-instant, so swapping between them feels quick. */
.mj-ui-pop { transition: opacity 200ms ${OUT}, transform 200ms ${OUT}; }
.mj-ui-pop[data-state="closed"] { opacity: 0; transform: scale(0.95); }
.mj-ui-pop[data-instant="true"] { transition-duration: 0ms; }

/* A sheet rises on the drawer curve; its scrim only fades. */
.mj-ui-sheet { transition: transform 260ms ${DRAWER}, opacity 200ms ${OUT}; }
.mj-ui-sheet[data-state="closed"] { transform: ${SHEET_OFFSCREEN}; }
.mj-ui-scrim { transition: opacity 200ms ${OUT}; }
.mj-ui-scrim[data-state="closed"] { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .mj-ui-pop, .mj-ui-press, .mj-ui-sheet { transform: none !important; }
  .mj-ui-pop[data-state="closed"], .mj-ui-sheet[data-state="closed"] { opacity: 0; }
  .mj-ui-pop, .mj-ui-sheet, .mj-ui-scrim { transition-duration: 150ms; }
}
`;

/**
 * Runs on mount rather than on open, so the rules are in the document long
 * before anything can be pressed. Never removed: a second menu on the page
 * would otherwise take them away from the first.
 */
export function useMenuStyles(): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
}
